import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AppShell } from '@/layouts/AppShell';
import { redirectToPlatformLogin } from '@/services/platform-login';
import { DashboardPage } from '@/modules/dashboard/DashboardPage';
import { NotFoundPage } from '@/modules/not-found/NotFoundPage';
import { LoginPage } from '@/modules/auth/LoginPage';
import { CustomersPage } from '@/modules/customers/CustomersPage';
import { AccountDetailPage } from '@/modules/customers/AccountDetailPage';
import { InvoicesPage } from '@/modules/invoices/InvoicesPage';
import { InvoiceDetailPage } from '@/modules/invoices/InvoiceDetailPage';
import { ReceivablesPage } from '@/modules/receivables/ReceivablesPage';
import { OutstandingDetailPage } from '@/modules/receivables/OutstandingDetailPage';
import { CollectionsPage } from '@/modules/collections/CollectionsPage';
import { ContractsPage } from '@/modules/contracts/ContractsPage';
import { ContractDetailPage } from '@/modules/contracts/ContractDetailPage';
import { SubscriptionsPage } from '@/modules/subscriptions/SubscriptionsPage';
import { SubscriptionDetailPage } from '@/modules/subscriptions/SubscriptionDetailPage';
import { QuotesPage } from '@/modules/quotes/QuotesPage';
import { QuoteDetailPage } from '@/modules/quotes/QuoteDetailPage';
import { ProposalsPage } from '@/modules/proposals/ProposalsPage';
import { ProposalDetailPage } from '@/modules/proposals/ProposalDetailPage';
import { PriceBooksPage } from '@/modules/setup/PriceBooksPage';
import { PriceBookDetailPage } from '@/modules/setup/PriceBookDetailPage';
import { RateCardsPage } from '@/modules/setup/RateCardsPage';
import { RateCardDetailPage } from '@/modules/setup/RateCardDetailPage';
import { RemindersPage } from '@/modules/setup/RemindersPage';
import { OrganizationPage } from '@/modules/setup/OrganizationPage';
import { useAuth } from '@/services/auth-context';

/**
 * Auth-state-aware route gate. Renders:
 *   - a centered spinner while the AuthProvider is rehydrating from
 *     localStorage on boot (avoids a /login flicker for returning users);
 *   - the children when authenticated;
 *   - a redirect to the canonical platform login when unauthenticated.
 */
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();

  // Kosh never renders its own login page — when unauthenticated, hard-
  // redirect to the canonical platform login at zafroi.com/login and let
  // the cross-subdomain refresh cookie restore the session on bounce-back.
  useEffect(() => {
    if (status === 'unauthenticated') {
      redirectToPlatformLogin();
    }
  }, [status]);

  if (status === 'loading' || status === 'unauthenticated') {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="login" element={<LoginPage />} />

      <Route
        element={
          <AuthGuard>
            <AppShell />
          </AuthGuard>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />

        {/* Catalog (Products/Plans/Offerings) moved to zafroi-catalog-app
            (ADR-035/036). Kosh keeps commerce/transactions only. */}
        <Route path="contracts" element={<ContractsPage />} />
        <Route path="contracts/:id" element={<ContractDetailPage />} />
        <Route path="subscriptions" element={<SubscriptionsPage />} />
        <Route path="subscriptions/:id" element={<SubscriptionDetailPage />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="quotes/:id" element={<QuoteDetailPage />} />
        <Route path="proposals" element={<ProposalsPage />} />
        <Route path="proposals/:id" element={<ProposalDetailPage />} />
        <Route path="setup/price-books" element={<PriceBooksPage />} />
        <Route path="setup/price-books/:id" element={<PriceBookDetailPage />} />
        <Route path="setup/rate-cards" element={<RateCardsPage />} />
        <Route path="setup/rate-cards/:id" element={<RateCardDetailPage />} />
        <Route path="setup/reminders" element={<RemindersPage />} />
        <Route path="setup/organization" element={<OrganizationPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:id" element={<AccountDetailPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="receivables" element={<ReceivablesPage />} />
        <Route path="receivables/:id" element={<OutstandingDetailPage />} />
        <Route path="collections" element={<CollectionsPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
